import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Upload, FileText, Zap, Shield, TrendingUp, Clock, BarChart3, Users, LineChart, Activity } from "lucide-react";
import Navigation from "@/components/Navigation";

export const HomePage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-accent/20 to-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-5 pointer-events-none" />
        <div className="container mx-auto px-4 pt-20 pb-32">
          <div className="max-w-4xl mx-auto text-center animate-fade-in-up">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent border border-primary/20 mb-8">
              <Activity className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-accent-foreground">Hub &Eacute;nerg&eacute;tique</span>
            </div>
            
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold font-display mb-6 leading-tight">
              Votre plateforme
              <span className="gradient-text"> de suivi &eacute;nerg&eacute;tique</span>
            </h1>
            
            <p className="text-lg sm:text-xl text-muted-foreground mb-10 max-w-2xl mx-auto leading-relaxed">
              Centralisez tous vos outils de pilotage &eacute;nerg&eacute;tique en un seul endroit. 
              Analysez, comparez et optimisez la consommation de vos clients pour leur offrir un accompagnement sur-mesure.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/dashboard" className="w-full sm:w-auto">
                <Button 
                  size="lg" 
                  className="w-full text-base px-8 py-6 shadow-lg hover:shadow-xl"
                  data-testid="home-dashboard-btn"
                >
                  <BarChart3 className="w-5 h-5 mr-2" />
                  Tableau de bord
                </Button>
              </Link>
              <Link to="/consumption/reports" className="w-full sm:w-auto">
                <Button 
                  size="lg" 
                  variant="outline"
                  className="w-full text-base px-8 py-6 shadow-lg hover:shadow-xl"
                >
                  <TrendingUp className="w-5 h-5 mr-2" />
                  Suivi Consommation
                </Button>
              </Link>
              <Link to="/reports" className="w-full sm:w-auto">
                <Button 
                  size="lg" 
                  variant="outline"
                  className="w-full text-base px-8 py-6 shadow-lg hover:shadow-xl"
                >
                  <FileText className="w-5 h-5 mr-2" />
                  Rapports TICFE
                </Button>
              </Link>
              <Link to="/optimization/reports" className="w-full sm:w-auto">
                <Button 
                  size="lg" 
                  variant="outline"
                  className="w-full text-base px-8 py-6 shadow-lg hover:shadow-xl"
                >
                  <Zap className="w-5 h-5 mr-2" />
                  Optimisation
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold font-display mb-4">Tous vos outils au m&ecirc;me endroit</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Un hub centralis&eacute; pour piloter la performance &eacute;nerg&eacute;tique de l'ensemble de votre portefeuille clients
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <FeatureCard
              icon={<BarChart3 className="w-8 h-8" />}
              title="Tableau de bord multi-client"
              description="Vue consolid&eacute;e de tous vos clients avec KPIs, alertes surconsommation et comparaisons ann&eacute;e sur ann&eacute;e."
            />
            
            <FeatureCard
              icon={<LineChart className="w-8 h-8" />}
              title="Suivi des consommations"
              description="Analysez les courbes de charge, d&eacute;tectez les d&eacute;rives et suivez l'&eacute;volution mois par mois pour chaque site."
            />
            
            <FeatureCard
              icon={<Zap className="w-8 h-8" />}
              title="Analyse IA int&eacute;gr&eacute;e"
              description="G&eacute;n&eacute;rez des analyses intelligentes par site avec recommandations personnalis&eacute;es pour vos clients."
            />
            
            <FeatureCard
              icon={<Users className="w-8 h-8" />}
              title="Gestion multi-franchis&eacute;s"
              description="Chaque franchis&eacute; acc&egrave;de uniquement &agrave; ses propres clients, l'administrateur voit l'ensemble du r&eacute;seau."
            />
            
            <FeatureCard
              icon={<Shield className="w-8 h-8" />}
              title="Rapports professionnels"
              description="G&eacute;n&eacute;rez des rapports PDF aux couleurs de votre marque, pr&ecirc;ts &agrave; envoyer &agrave; vos clients."
            />
            
            <FeatureCard
              icon={<Clock className="w-8 h-8" />}
              title="Historique complet"
              description="Retrouvez l'ensemble de vos rapports et donn&eacute;es historiques pour un suivi dans la dur&eacute;e."
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <Card className="max-w-4xl mx-auto bg-gradient-to-br from-secondary to-secondary/90 border-0 shadow-2xl">
            <CardContent className="p-12 text-center">
              <h2 className="text-3xl sm:text-4xl font-bold font-display mb-4 text-secondary-foreground">
                Offrez le meilleur suivi &eacute;nerg&eacute;tique &agrave; vos clients
              </h2>
              <p className="text-lg text-secondary-foreground/90 mb-8 max-w-2xl mx-auto">
                Acc&eacute;dez &agrave; votre tableau de bord et commencez &agrave; piloter la performance &eacute;nerg&eacute;tique de votre portefeuille.
              </p>
              <Link to="/dashboard">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="bg-background text-foreground hover:bg-background/90 px-8 py-6 text-base"
                >
                  <BarChart3 className="w-5 h-5 mr-2" />
                  Acc&eacute;der au tableau de bord
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-secondary/5 py-12 border-t">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <img 
                src="/logo-pde.png" 
                alt="Place des &Eacute;nergies" 
                className="h-12 w-auto"
              />
            </div>
            
            <p className="text-sm text-muted-foreground">
              &copy; 2026 Place des &Eacute;nergies. Tous droits r&eacute;serv&eacute;s.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

const FeatureCard = ({ icon, title, description }) => {
  return (
    <Card className="group hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
      <CardContent className="p-8 flex flex-col">
        <div className="w-14 h-14 bg-accent rounded-xl flex items-center justify-center mb-6 text-primary group-hover:scale-110 transition-transform duration-300">
          {icon}
        </div>
        <h3 className="text-xl font-semibold font-display mb-3">{title}</h3>
        <p className="text-muted-foreground leading-relaxed">{description}</p>
      </CardContent>
    </Card>
  );
};

export default HomePage;
